import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  LucideAngularModule,
  CheckCircle2,
  ChevronDown,
  BookOpen,
  Award,
} from 'lucide-angular';
import { ScoringGuidePreview, ScoringItemPreview } from '../../grading.types';
import { ScoringGuideTableComponent } from './components/scoring-guide-table/scoring-guide-table';
import { AmplitudeService } from '../../../../../common/analytics/amplitude.service';

@Component({
  selector: 'app-scoring-guide-preview',
  standalone: true,
  imports: [CommonModule, LucideAngularModule, ScoringGuideTableComponent],
  templateUrl: './scoring-guide-preview.component.html',
  styleUrl: './scoring-guide-preview.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScoringGuidePreviewComponent {
  private readonly amplitudeService = inject(AmplitudeService);

  readonly preview = input.required<ScoringGuidePreview>();

  protected readonly icons = {
    CheckCircle2,
    ChevronDown,
    BookOpen,
    Award,
  };

  // Track expanded sections (all collapsed by default)
  protected readonly expandedSections = signal<Set<string>>(new Set());

  /**
   * Get subtitle text with summary info
   */
  protected getSubtitle(): string {
    const summary = this.preview().summary;
    return `${summary.totalQuestions} questions, ${summary.totalPoints} points`;
  }

  /**
   * Toggle section expansion
   */
  protected toggleSection(sectionId: string): void {
    const current = this.expandedSections();
    const newSet = new Set(current);
    const isExpanding = !newSet.has(sectionId);

    if (isExpanding) {
      newSet.add(sectionId);
    } else {
      newSet.delete(sectionId);
    }
    this.expandedSections.set(newSet);

    this.amplitudeService.trackEvent('grading_scoring_guide_section_toggled', {
      sectionId,
      isExpanded: isExpanding,
    });
  }

  /**
   * Check if section is expanded
   */
  protected isSectionExpanded(sectionId: string): boolean {
    return this.expandedSections().has(sectionId);
  }

  /**
   * Get item type label
   */
  protected getItemTypeLabel(type: string): string {
    const labels: Record<string, string> = {
      rubric: 'Rubric',
      logic: 'Logic',
      exact_match: 'Exact Match',
    };
    return labels[type] || type;
  }

  /**
   * Get max points for an item
   */
  protected getItemMaxPoints(item: ScoringItemPreview): number {
    return item.criteria.reduce((sum, c) => {
      const maxLevel = c.levels[c.levels.length - 1];
      return sum + (maxLevel?.points ?? 0);
    }, 0);
  }
}
